import React, { CSSProperties } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import Model from "~/Model";
import OutputText from "~/voice_utils/TextOutput";
import RubyText from "~/components/utils/RubyText";
import ThoughtBubble from "~/components/animation/ThoughtBubble";
import FaceDetection from "~/components/FaceDetection";

interface ModelViewLayout2Props {
  visible: boolean;
  handleModelViewed: () => void;
  name: string;
  isSoundOn: boolean;
  toggleMute: () => void;
  isFaceDetection: boolean;
  setPrevCharacter: () => void | Promise<void>;
  setNextCharacter: () => void | Promise<void>;
  changeButtonsDisabled: boolean;
}

/**
 * 3Dモデルと関連UIを表示するコンポーネント
 */
// レンダラー設定やカメラ・ライト設定（UI 固定値）はローカルに保持
const canvasStyle: CSSProperties = {
  borderRadius: "10px",
  display: "block",
  height: "60% !important",
};

const cameraSettings = {
  fov: 20,
  near: 0.1,
  far: 1000,
  position: { x: 0, y: -0.05, z: 3 },
};

const lightSettings = {
  directional: {
    position: { x: 0, y: 10, z: 10 },
    color: 0xffffff,
    intensity: 3.0,
  },
  ambient: { color: 0xffffff, intensity: 0.5 },
};

const glConfig = {
  antialias: true,
  alpha: true,
};

const ModelViewLayout2: React.FC<ModelViewLayout2Props> = ({
  handleModelViewed,
  name,
  isSoundOn,
  toggleMute,
  isFaceDetection,
  setPrevCharacter,
  setNextCharacter,
  changeButtonsDisabled,
}) => {
  return (
    <div className="character-area-layout-2">
      <div className="character-controls-layout-2">
        <div className="nav-section-layout-2">
          <div className="character-name-mobile">
            <RubyText text={name} />
          </div>

          {/* 前のキャラクターボタン */}
          <button
            className="btn btn-circle nav-btn-layout-2"
            onMouseUp={() => void setPrevCharacter()}
            disabled={changeButtonsDisabled}
          >
            <span className="material-symbols-rounded">keyboard_arrow_left</span>
          </button>

          {/* キャラクター表示エリア */}
          <div className="character-center-layout-2">
            {/* 3Dモデル表示領域 */}
            <div className="chatpage-canvas-layout-2">
              <Canvas
                className={"main-canvas visible"}
                // カメラ設定の使用箇所
                camera={{
                  fov: cameraSettings.fov,
                  near: cameraSettings.near,
                  far: cameraSettings.far,
                  position: [
                    cameraSettings.position.x,
                    cameraSettings.position.y,
                    cameraSettings.position.z,
                  ],
                }}
                flat
                style={canvasStyle}
                gl={glConfig}
              >
                {/* ライト設定の使用箇所 */}
                {/* eslint-disable react/no-unknown-property */}
                <directionalLight
                  position-x={lightSettings.directional.position.x}
                  position-y={lightSettings.directional.position.y}
                  position-z={lightSettings.directional.position.z}
                  color={lightSettings.directional.color}
                  intensity={lightSettings.directional.intensity}
                />
                <OrbitControls
                  enableZoom={true}
                  enablePan={false}
                  enableRotate={true}
                  target={[0, 0, 0]}
                  minDistance={3}
                  maxDistance={10}
                />
                <ambientLight
                  color={lightSettings.ambient.color}
                  intensity={lightSettings.ambient.intensity}
                />
                <Model onViewed={handleModelViewed} />
              </Canvas>
              <div className="character-overlay">
                <ThoughtBubble />
              </div>
            </div>

            {/* 横画面用のキャラクター名（下部に配置） */}
            <div className="character-name">
              <RubyText text={name} />
            </div>
          </div>

          {/* 次のキャラクターボタン */}
          <button
            className="btn btn-circle nav-btn-layout-2"
            onMouseUp={() => void setNextCharacter()}
            disabled={changeButtonsDisabled}
          >
            <span className="material-symbols-rounded">keyboard_arrow_right</span>
          </button>
        </div>

        {/* 右側：コントロール部分 */}
        <div className="control-section-layout-2">
          {/* 音量ボタン（横画面用） */}
          <button
            className={`volume-btn-layout-2 ${isSoundOn ? "sound-on" : "sound-off"}`}
            onClick={toggleMute}
          >
            {isSoundOn ? (
              <span className="material-symbols-rounded sound-on-btn">volume_up</span>
            ) : (
              <span className="material-symbols-rounded sound-off-btn">volume_off</span>
            )}
          </button>
        </div>
      </div>

      {/* 縦画面用音量ボタン */}
      <button
        className={`volume-btn-mobile-layout-2 ${isSoundOn ? "sound-on" : "sound-off"}`}
        onClick={toggleMute}
      >
        {isSoundOn ? (
          <span className="material-symbols-rounded sound-on-btn">volume_up</span>
        ) : (
          <span className="material-symbols-rounded sound-off-btn">volume_off</span>
        )}
      </button>

      {/* 音声出力エリア */}
      <div className="speech-area">
        <OutputText />
      </div>

      {/* 顔検出機能 */}
      {isFaceDetection && <FaceDetection />}
    </div>
  );
};

export default ModelViewLayout2;
